// src/pages/DataModal.jsx
import React, { useEffect, useState, useCallback, useRef } from "react";
import "./confirmation.css";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { extractSlugPart, unSlugify } from "../utils/helperFunctions";

const DataModal = ({
  setErrorOpen,
  errorOpen,
  formState,
  setFormState,
  setOverrideState,
  onComplete, // will be called with { allYes: boolean }
  onNo, // optional: called with the currentKey when user chooses "No"
}) => {
  const [errorMessage1, setErrorMessage1] = useState("");
  const [errorMessage2, setErrorMessage2] = useState("");
  const [errorIndex, setErrorIndex] = useState(0);

  const user = useSelector((state) => state.user);
  const location = useLocation();

  const companyName =
    user.companyName === undefined
      ? unSlugify(extractSlugPart(location.pathname))
      : user.companyName;

  // Track if any "No" happened during this modal session
  const hadAnyNoRef = useRef(false);

  const currentKey = errorOpen?.[errorIndex];
  const isLast = errorOpen && errorIndex === errorOpen.length - 1;

  // Apply the actual form reset change when the user clicks "No"
  const applyNoAction = useCallback(
    (key) => {
      if (!key) return;
      setFormState((prev) => {
        const next = { ...prev };
        switch (key) {
          // Facilities: "No" means user should CHECK these
          case "cam":
          case "tax": {
            const arr = Array.isArray(prev?.facility) ? prev.facility : [];
            next.facility = Array.from(new Set([...arr, key]));
            break;
          }
          // Amenities: "No" means user should UNCHECK these
          case "meeting":
          case "kitchen":
          case "copy":
          case "receptionist": {
            const arr = Array.isArray(prev?.amenity) ? prev.amenity : [];
            next.amenity = arr.filter((v) => v !== key);
            break;
          }
          // Numeric prompts—leave values as-is; user must fix in the form
          case "desks":
          case "sqftLeased":
          default:
            break;
        }
        return next;
      });

      // Make sure this confirm will be required again later unless user fixes it
      setOverrideState((prev) => ({ ...prev, [key]: false }));
    },
    [setFormState, setOverrideState]
  );

  const finishOrNext = useCallback(() => {
    if (!errorOpen || errorOpen.length === 0) return;

    if (!isLast) {
      setErrorIndex((i) => i + 1);
      return;
    }

    // Last step
    const hadNo = hadAnyNoRef.current;
    hadAnyNoRef.current = false; // reset for next time

    setErrorIndex(0);
    setErrorOpen(false);

    // Always notify parent with an explicit payload
    if (typeof onComplete === "function") {
      onComplete({ allYes: !hadNo });
    }
  }, [errorOpen, isLast, onComplete, setErrorOpen]);

  // Compose messages
  useEffect(() => {
    if (!errorOpen || errorOpen.length === 0 || !formState) return;
    const sqftPerDesk =
      formState?.desks > 0
        ? Math.round((formState.sqftLeased / formState.desks) * 100) / 100
        : 0;
    const howHigh = sqftPerDesk > 300 ? "very high" : "higher than average";

    switch (currentKey) {
      case "sqftLeased":
        setErrorMessage1(
          `The average square feet leased is ${sqftPerDesk} which is ${howHigh}. Please include only the square feet used to calculate your lease payment that is 100% dedicated to ${companyName} and do not include any square feet shared with ${
            formState.serviceProvider ? formState.serviceProvider : "the Lessor"
          } or with other tenants such as hallways, restrooms, kitchens, lobbies, meeting rooms, reception areas, etc.`
        );
        setErrorMessage2("Is your entry correct?");
        break;
      case "cam":
        setErrorMessage1(
          `We noticed that "Common Area Maintenance" is not checked on the form. Most desk rentals or office leases include this item in the lease payment.`
        );
        setErrorMessage2(
          `Will ${companyName} pay Common Area Maintenance separately and in addition to the lease payment? If not, click 'No', ensure the box is checked, and click 'Save & Continue'.`
        );
        break;
      case "tax":
        setErrorMessage1(
          `We noticed that "Property Insurance and Taxes" is not checked on the form. Most desk rentals or office leases include this item in the lease payment.`
        );
        setErrorMessage2(
          `Will ${companyName} pay Property Insurance and Taxes separately and in addition to the lease payment? If not, click 'No', ensure the box is checked, and click 'Save & Continue'.`
        );
        break;
      case "meeting":
        setErrorMessage1(
          `Is the furnished meeting/conference room shared with ${formState.serviceProvider}?`
        );
        setErrorMessage2(
          `If it is included in the SF leased, click 'No' and deselect the checkbox to continue.`
        );
        break;
      case "kitchen":
        setErrorMessage1(
          `Is the Kitchen shared with ${formState.serviceProvider}?`
        );
        setErrorMessage2(
          `If it is included in the SF leased, click 'No' and deselect the checkbox to continue.`
        );
        break;
      case "furnished":
        setErrorMessage1(
          `Is the furniture provided by ${formState.serviceProvider}?`
        );
        setErrorMessage2(
          `If it is provided by ${companyName}, click 'No' and deselect the checkbox to continue.`
        );
        break;
      case "copy":
        setErrorMessage1(
          `Is the copy/fax/print/scan machine provided by ${formState.serviceProvider}?`
        );
        setErrorMessage2(
          `If it is provided by ${companyName}, click 'No' and deselect the checkbox to continue.`
        );
        break;
      case "receptionist":
        setErrorMessage1(
          `Is the receptionist answering service provided by ${formState.serviceProvider}?`
        );
        setErrorMessage2(
          `If it is provided by ${companyName}, click 'No' and deselect the checkbox to continue.`
        );
        break;
      case "desks":
        setErrorMessage1(
          `You’ve entered ${formState.desks} desks/workstations. Are all of these dedicated full-time to ${companyName}?`
        );
        setErrorMessage2(
          `If not, click 'No' and reduce the number of desks to only those that are used full-time by ${companyName}.`
        );
        break;
      default:
        setErrorMessage1("");
        setErrorMessage2("");
        break;
    }
  }, [currentKey, errorOpen, formState, companyName]);

  // Keyboard: Enter = Yes, Esc = No (Esc also blocks auto-submit)
  useEffect(() => {
    if (!errorOpen || errorOpen.length === 0) return;
    const onKey = (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        if (currentKey) {
          setOverrideState((prev) => ({ ...prev, [currentKey]: true }));
        }
        finishOrNext();
      }
      if (e.key === "Escape") {
        e.preventDefault();
        hadAnyNoRef.current = true;
        applyNoAction(currentKey); // keep behavior consistent with clicking "No"
        if (onNo && currentKey) onNo(currentKey);
        finishOrNext();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    errorOpen,
    currentKey,
    setOverrideState,
    finishOrNext,
    applyNoAction,
    onNo,
  ]);

  if (!errorOpen || errorOpen.length === 0) return null;

  const step = errorIndex + 1;
  const total = errorOpen.length;
  const yesLabel =
    isLast && !hadAnyNoRef.current
      ? currentKey === "sqftLeased"
        ? "Yes"
        : "Yes & Continue"
      : "Yes";

  return (
    <div
      className="modal-container"
      role="dialog"
      aria-modal="true"
      aria-labelledby="data-modal-title"
    >
      <div className="my-modal">
        <h2 id="data-modal-title" className="modal-title">
          Are you sure? ({step} of {total})
        </h2>

        <p className="modal-subtext modal-p">{errorMessage1}</p>

        {errorMessage2 && <p className="modal-text">{errorMessage2}</p>}

        <div className="modal-btn-cont">
          <button
            className="modal-cancel"
            type="button"
            onClick={() => {
              hadAnyNoRef.current = true;
              applyNoAction(currentKey);
              if (onNo && currentKey) onNo(currentKey);
              finishOrNext();
            }}
          >
            No
          </button>

          <button
            className="modal-delete"
            type="button"
            onClick={() => {
              if (currentKey) {
                setOverrideState((prev) => ({ ...prev, [currentKey]: true }));
              }
              finishOrNext();
            }}
          >
            {yesLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DataModal;
